import axios, { AxiosError, AxiosResponse } from "axios"
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { getBaseEndpoint } from "../../features/endpoints/endpointManager"
import {
  InsuranceCarrier,
  Provider,
  ProviderDisclosure,
  ProviderLicense,
  ProviderNameResult,
  ProviderPhoto,
} from "./schema"
import { ProviderState, providerSlice, selectProvider } from "./model"
import {
  blobToBase64,
  downloadFileBlob,
  getFilenameFromContentDisposition,
} from "../../features/shared/utils"
import {
  bannersReset,
  errorHandlerUpdated,
} from "../../features/errors/errorSlice"
import { PracticeName } from "../Practice/loaders/loader"
import { RootState } from "../../reduxStore/store"
import { selectContent } from "../CMS/model"
import appInsightsLogger from "../../features/logging/appInsightsLogger"
import { DataStatus } from "../enums"
import { apiService } from "../../features/shared/apiService"
import { PracticeRole, PracticeRoleSet } from "../Account/userSlice"
import {
  extendPracticeInvites,
  fetchCompletedUserSteps,
  fetchPartialPracticesByUserId,
  flattenAffiliateInvites,
  getIsSeeingPatients,
} from "../../pages/PracticeInvitations/usePracticeInvitesByUserId"
import {
  autoSelectAffiliatedOrInvitePracticeId,
  getOnboardingFlowIdFromState,
  isUserOnboarded,
} from "../Practices/helpers"
import { PartialPractice } from "../Practices/schema"
import { FileStatusDisplayIdEnum } from "../Files/schema"
import { getFullUserName } from "../Account/helpers"
import {
  stitchDisclosuresFullName,
  stitchProviderPhotoFullName,
} from "./helpers"
import { cloneDeep, keyBy } from "lodash"
import { downloadZip } from "../Files/helpers"
import {
  getBase64ImageFromStorage,
  saveBase64ImageWithEviction,
} from "../../features/shared/localStorageBase64"

export interface ProviderDataStatus {
  baseProviderDataStatus: DataStatus
  providerNamesDataStatus: DataStatus
  providerPracticeNamesDataStatus: DataStatus
  providerOnboardingDataStatus: DataStatus
}

const initialDataStatuses: ProviderDataStatus = {
  baseProviderDataStatus: DataStatus.LOADING,
  providerNamesDataStatus: DataStatus.LOADING,
  providerPracticeNamesDataStatus: DataStatus.LOADING,
  providerOnboardingDataStatus: DataStatus.LOADING,
}

interface ProviderLoaderState {
  dataStatuses: ProviderDataStatus
}

const initialLoaderState: ProviderLoaderState = {
  dataStatuses: initialDataStatuses,
}

export const providerLoaderSlice = createSlice({
  name: "providerLoader",
  initialState: initialLoaderState,
  reducers: {
    providerDataStatusUpdated: (
      state: ProviderLoaderState,
      { payload }: PayloadAction<Partial<ProviderDataStatus>>,
    ) => {
      state.dataStatuses = {
        ...state.dataStatuses,
        ...payload,
      }
    },
  },
})

export default providerLoaderSlice.reducer

// Export actions and reducer
export const { providerDataStatusUpdated } = providerLoaderSlice.actions

export const selectProviderDataStatus = (state: {
  providerLoader: ProviderLoaderState
}): ProviderDataStatus => {
  return state.providerLoader.dataStatuses
}
export const selectProviderIsLoadingOrSending = (state: {
  providerLoader: ProviderLoaderState
}): boolean => {
  return Object.values(state.providerLoader.dataStatuses).some(
    (status) => status === DataStatus.LOADING || status === DataStatus.SENDING,
  )
}

const getProviderNamesEndpoint = getBaseEndpoint() + `/provider/providers`

export const getProviderNameThunk = createAsyncThunk(
  "providerHeader/getProvider",
  async (_args, { dispatch }): Promise<ProviderNameResult | undefined> => {
    try {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerNamesDataStatus: DataStatus.SENDING,
        }),
      )

      const response = await axios.get<ProviderNameResult>(
        getProviderNamesEndpoint,
      )

      if (response.status === 200) {
        dispatch(
          providerSlice.actions.providerNameUpdated(
            response.data as ProviderNameResult,
          ),
        )
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            providerNamesDataStatus: DataStatus.LOADED,
          }),
        )
        return response.data
      } else {
        throw new Error(
          `Failed to get provider names with status code: ${response.status}`,
        )
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response &&
        error.response.status === 404
      ) {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            providerNamesDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            providerNamesDataStatus: DataStatus.ERROR,
          }),
        )
        console.error(error)
      }
      return undefined
    }
  },
)

interface providerPracticeNamesThunkArgs {
  userId?: string
  providerId?: string
}
const getProviderPracticeNamesEndpoint = (userId: string): string => {
  return getBaseEndpoint() + `/practice/practices/names/${userId}`
}

const getProviderPracticeNamesByProviderIdEndpoint = (
  providerId: string,
): string => {
  return (
    getBaseEndpoint() + `/practice/practices/names/unregistered/${providerId}`
  )
}

// TODO: refactor to use Zager's changes
const getProviderTableDataEndpoint = getBaseEndpoint() + `/provider/tabledata`
export type TempTableDataRes =
  | {
      payload: {
        insuranceCarriers: InsuranceCarrier[]
      }
    }
  | undefined
export const getProviderTableDataThunk = createAsyncThunk(
  "providerHeader/getProviderTableData",
  async (_props, { dispatch }) => {
    try {
      const response = await axios.get<TempTableDataRes>(
        getProviderTableDataEndpoint,
      )
      if (response.status === 200) {
        const payload: TempTableDataRes = response.data as TempTableDataRes
        return payload
      } else {
        throw new Error(
          `Failed to get provider practice names with status code: ${response.status}`,
        )
      }
    } catch (error) {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerPracticeNamesDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)

export const getProviderPracticeNamesThunk = createAsyncThunk(
  "providerHeader/getProviderPracticeNames",
  async (
    { userId, providerId }: providerPracticeNamesThunkArgs,
    { dispatch },
  ) => {
    try {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerPracticeNamesDataStatus: DataStatus.SENDING,
        }),
      )

      // If there is no userId, which happens with migrated Providers, don't call this API with an empty string
      // This causes an issue with AR users, as the API called is /practicenames instead of /practicenames/{userId},
      // and will return the entire list of practice's names in the DB.
      // TODO: Address this issues in https://dev.azure.com/HACloud/VEAP/_workitems/edit/9223
      let response
      if (userId) {
        response = await axios.get<PracticeName[]>(
          getProviderPracticeNamesEndpoint(userId),
        )
      } else if (providerId) {
        response = await axios.get<PracticeName[]>(
          getProviderPracticeNamesByProviderIdEndpoint(providerId),
        )
      } else {
        response = {
          status: 200,
          data: [],
        }
      }
      if (response.status === 200) {
        const payload: PracticeName[] = response.data as PracticeName[]
        // Sort for display in the dropdown
        payload.sort((a, b) => a.name.localeCompare(b.name))
        dispatch(providerSlice.actions.providerPracticeNamesUpdated(payload))
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            providerPracticeNamesDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error(
          `Failed to get provider practice names with status code: ${response.status}`,
        )
      }
    } catch (error) {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerPracticeNamesDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)

export const getProviderPracticeRolesThunk = createAsyncThunk(
  "practiceHeader/getProviderPracticeRoles",
  async (
    {
      userId,
      providerId,
      setDataStatus,
    }: {
      userId?: string
      providerId?: string
      setDataStatus?: (status: DataStatus) => void
    },
    { dispatch },
  ) => {
    try {
      setDataStatus?.(DataStatus.SENDING)
      let response: { id: string; roles: PracticeRole[] }[] | []
      if (userId) {
        response = await apiService.practice.get.PRACTICE_ROLES_BY_USER_ID({
          userId,
        })
      } else if (providerId) {
        response =
          await apiService.practice.get.PRACTICE_ROLES_BY_UNREGISTERED_PROVIDER_ID(
            {
              providerId,
            },
          )
      } else {
        response = []
      }
      if (response.length > 0) {
        setDataStatus?.(DataStatus.LOADED)
        const payload: PracticeRoleSet[] = response.map((item) => ({
          practiceId: item.id,
          roles: item.roles,
        }))
        dispatch(providerSlice.actions.providerPracticeRolesUpdated(payload))
      } else {
        setDataStatus?.(DataStatus.ERROR)
        appInsightsLogger.traceError(
          `No practice roles found for userId: ${userId}`,
        )
        throw new Error(`No practice roles found for userId: ${userId}`)
      }
    } catch (error) {
      console.error(error)
    }
  },
)

interface providerThunkArgs {
  providerId: string
  setDataStatus?: (status: DataStatus) => void
}
export const getBaseProviderEndpoint = (providerId: string): string => {
  return getBaseEndpoint() + `/provider/providers/${providerId}`
}

const getThumbnailFailureStatus = (error: unknown): number | undefined => {
  return axios.isAxiosError(error) ? error.response?.status : undefined
}

const traceProviderThumbnailHydrationFailure = (
  providerId: string,
  error: unknown,
) => {
  const status = getThumbnailFailureStatus(error)
  const statusText = status ? ` status ${status}` : ""
  appInsightsLogger.traceError(
    `Provider photo thumbnail hydration failed for provider ${providerId}${statusText}. Continuing without a renderable provider photo.`,
  )
}

const buildRenderableProviderPhoto = async ({
  base64String,
  haAdminText,
  providerPhoto,
}: {
  base64String: string
  haAdminText?: string
  providerPhoto?: ProviderPhoto
}): Promise<ProviderPhoto | undefined> => {
  const stitchedPhoto = await stitchProviderPhotoFullName(
    providerPhoto,
    haAdminText,
  )

  return stitchedPhoto
    ? {
        ...stitchedPhoto,
        fileStatusDisplayId: FileStatusDisplayIdEnum.AVAILABLE,
        thumbnailBase64: base64String,
      }
    : undefined
}

const hydrateProviderPhotoThumbnail = async ({
  haAdminText,
  providerResponse,
}: {
  haAdminText?: string
  providerResponse: Provider
}): Promise<{
  photoFileStatusDisplayId?: FileStatusDisplayIdEnum
  providerPhoto?: ProviderPhoto
}> => {
  if (!providerResponse.photoFileId) {
    return {
      photoFileStatusDisplayId: undefined,
      providerPhoto: undefined,
    }
  }

  if (
    providerResponse.photoFileStatusDisplayId ===
    FileStatusDisplayIdEnum.QUARANTINED
  ) {
    return {
      photoFileStatusDisplayId: FileStatusDisplayIdEnum.QUARANTINED,
      providerPhoto: undefined,
    }
  }

  try {
    const cachedBase64 = getBase64ImageFromStorage(providerResponse.photoFileId)
    if (cachedBase64) {
      return {
        photoFileStatusDisplayId: FileStatusDisplayIdEnum.AVAILABLE,
        providerPhoto: await buildRenderableProviderPhoto({
          base64String: cachedBase64,
          haAdminText,
          providerPhoto: providerResponse.providerPhoto,
        }),
      }
    }

    const url: string = getProviderPhotoThumbnailEndpoint(providerResponse.id!)
    const photoResponse: AxiosResponse<Blob> = await axios.get(url, {
      responseType: "blob",
    })

    if (photoResponse.status === 202) {
      return {
        photoFileStatusDisplayId: FileStatusDisplayIdEnum.PROCESSING,
        providerPhoto: undefined,
      }
    }

    if (photoResponse.status !== 200) {
      throw new Error(
        `Provider photo thumbnail returned status ${photoResponse.status}`,
      )
    }

    const fileBlob: Blob = photoResponse.data
    const base64String = await blobToBase64(fileBlob)
    saveBase64ImageWithEviction(providerResponse.photoFileId, base64String)

    return {
      photoFileStatusDisplayId: FileStatusDisplayIdEnum.AVAILABLE,
      providerPhoto: await buildRenderableProviderPhoto({
        base64String,
        haAdminText,
        providerPhoto: providerResponse.providerPhoto,
      }),
    }
  } catch (error) {
    traceProviderThumbnailHydrationFailure(providerResponse.id!, error)
    return {
      photoFileStatusDisplayId: undefined,
      providerPhoto: undefined,
    }
  }
}

export const fetchBaseProvider = createAsyncThunk(
  "providerLoader/fetchBaseProvider",
  async (
    { providerId, setDataStatus }: providerThunkArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState

    try {
      if (!!setDataStatus) {
        setDataStatus(DataStatus.SENDING)
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.SENDING,
          }),
        )
      }
      const response = await axios.get<Provider>(
        getBaseProviderEndpoint(providerId),
      )
      if (response.status === 200) {
        const providerResponse = response.data
        let updatedProvider: Provider = cloneDeep(providerResponse)

        // Stitch photo fields
        const { photoFileStatusDisplayId, providerPhoto } =
          await hydrateProviderPhotoThumbnail({
            providerResponse,
            haAdminText: state.contentManagement.content.common?.haAdmin,
          })

        // stitch disclosure fields
        let updatedDisclosures: ProviderDisclosure[] = []
        if (providerResponse.providerDisclosures.length > 0) {
          updatedDisclosures = await stitchDisclosuresFullName(
            providerResponse.providerDisclosures || [],
            state.contentManagement.content.common?.haAdmin,
          )
        }

        updatedProvider = {
          ...updatedProvider,
          providerDisclosures: updatedDisclosures,
          photoFileStatusDisplayId,
          providerPhoto,
        }

        dispatch(providerSlice.actions.providerUpdated(updatedProvider))

        await dispatch(getProviderOnboardingData(providerResponse))

        if (!!setDataStatus) {
          setDataStatus(DataStatus.LOADED)
        } else {
          dispatch(
            providerLoaderSlice.actions.providerDataStatusUpdated({
              baseProviderDataStatus: DataStatus.LOADED,
            }),
          )
        }
        return updatedProvider
      } else {
        throw new Error(
          `Failed to fetch base provider with status code: ${response.status}`,
        )
      }
    } catch (error) {
      if (!!setDataStatus) {
        setDataStatus(DataStatus.ERROR)
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.ERROR,
          }),
        )
      }
      console.error(error)
    }
  },
)

export const getProviderOnboardingData = createAsyncThunk(
  "providerLoader/getProviderOnboardingData",
  async (provider: Provider, { dispatch }) => {
    try {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerOnboardingDataStatus: DataStatus.LOADING,
        }),
      )

      const userId = provider.userId
      const [affiliates, partialPractices, completedUserOnboardingSteps] =
        await Promise.all([
          apiService.practice.get.PRACTICE_AFFILIATES_BY_USER_ID({ userId }),
          fetchPartialPracticesByUserId(userId),
          fetchCompletedUserSteps(userId),
        ])

      const practiceInvites = flattenAffiliateInvites(affiliates)
      const extendedPracticeInvites = extendPracticeInvites(
        practiceInvites,
        partialPractices,
      )
      const autoSelectedPracticeIdResult =
        autoSelectAffiliatedOrInvitePracticeId(
          partialPractices,
          extendedPracticeInvites,
        )
      const isSeeingPatients = getIsSeeingPatients(
        provider,
        autoSelectedPracticeIdResult,
        extendedPracticeInvites,
      )
      const flowId = getOnboardingFlowIdFromState({
        partialPractices,
        provider,
        detachedProviders: [],
        practiceInvites: extendedPracticeInvites,
        autoSelectedPracticeIdResult,
        isSeeingPatients,
      })
      const affiliatedPractice = partialPractices.find(
        (practice: PartialPractice) =>
          practice.id === autoSelectedPracticeIdResult?.practiceId,
      )
      const userOnboarded = isUserOnboarded(
        flowId,
        completedUserOnboardingSteps,
        affiliatedPractice,
        provider,
        false,
      )
      const providerOnboardingData = {
        affiliatedPractice,
        provider,
        userOnboarded,
      }
      dispatch(
        providerSlice.actions.providerOnboardingDataUpdated(
          providerOnboardingData,
        ),
      )

      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerOnboardingDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      appInsightsLogger.traceError(error)

      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          providerOnboardingDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

// TODO: CMS cleanup
export const getProviderFile = createAsyncThunk(
  "providerLoader/getProviderFile",
  async (
    props: {
      providerId: string | undefined
      fileId: string | undefined
      setDataStatus?: (status: DataStatus) => void
      downloadFileNameNoExt?: string
    },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const commonText = state.contentManagement.content.common

    dispatch(bannersReset())
    const { providerId, fileId, setDataStatus, downloadFileNameNoExt } = props
    if (!providerId || !fileId) {
      throw new Error("Provider ID and File ID are required")
    }
    const url =
      getBaseEndpoint() +
      `/provider/providers/${providerId}/files/${fileId}/content`

    setDataStatus?.(DataStatus.SENDING)
    const response = await axios.get(url, { responseType: "blob" })
    if (response.status === 200) {
      const fileBlob: Blob = response.data
      const contentDisposition = response.headers["content-disposition"]
      const filename =
        downloadFileNameNoExt ||
        getFilenameFromContentDisposition(contentDisposition)

      downloadFileBlob(fileBlob, filename)
      setDataStatus?.(DataStatus.LOADED)
    } else if (response.status == 202) {
      dispatch(
        errorHandlerUpdated({
          infoBanner: {
            isVisible: true,
            title: "Warning",
            content: commonText?.fileProcessing || "",
          },
        }),
      )
      setDataStatus?.(DataStatus.LOADED)
    } else {
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: "Error",
            content: commonText?.fileDownloadError || "",
          },
        }),
      )
      setDataStatus?.(DataStatus.ERROR)
    }
  },
)

const getPostBaseProviderEndpoint = (providerId: string) => {
  return getBaseEndpoint() + `/provider/providers/${providerId}`
}

export const createBaseProvider = createAsyncThunk(
  "providerLoader/createBaseProvider",
  async (payload: Provider, { dispatch }) => {
    try {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          baseProviderDataStatus: DataStatus.SENDING,
        }),
      )
      const response = await axios.post<Provider>(
        getBaseEndpoint() + "/provider/providers",
        payload,
      )
      if (response.status === 201) {
        await dispatch(providerSlice.actions.providerUpdated(response.data))
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error(
          `Failed to create base provider with status code: ${response.status}`,
        )
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response &&
        error.response.status === 409
      ) {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.ERROR,
          }),
        )
      }
      console.error(error)
    }
  },
)

interface UpdateBaseProviderArgs {
  payload: Provider
  setDataStatus?: (status: DataStatus) => void
}

export const updateBaseProvider = createAsyncThunk(
  "practiceLoader/updateBaseProvider",
  async (
    { payload, setDataStatus }: UpdateBaseProviderArgs,
    { dispatch, getState },
  ) => {
    try {
      const state = getState() as { provider: ProviderState }
      const provider: Provider = selectProvider(state)
      const photoFileStatusDisplayId = provider.photoFileStatusDisplayId
      const photoFileName = provider.photoFileName
      const providerPhotoCreatedByFullName =
        provider.providerPhoto?.createdByFullName ?? ""
      const currentProviderDisclosures = provider.providerDisclosures ?? []
      const disclosuresById: { [id: string]: ProviderDisclosure } = keyBy(
        currentProviderDisclosures,
        "id",
      )

      if (setDataStatus) {
        setDataStatus(DataStatus.PENDING)
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.UPDATING,
          }),
        )
      }
      const response = await axios.put<Provider>(
        getPostBaseProviderEndpoint(payload.id!),
        payload,
      )
      if (response.status === 200 || response.status === 201) {
        const providerData = response.data
        const providerPhoto = providerData.providerPhoto
          ? {
              ...providerData.providerPhoto,
              createdByFullName: providerPhotoCreatedByFullName,
            }
          : undefined
        const updatedProvider: Provider = {
          ...providerData,
          // The provider photo fields below are not stored in the Provider DB table
          // but are part of Provider state.  We need to set them below to ensure
          // their value is not lost.
          photoFileStatusDisplayId: photoFileStatusDisplayId,
          photoFileName: photoFileName,
          providerPhoto: providerPhoto,
          providerDisclosures: providerData.providerDisclosures.map(
            (disclosure) => ({
              ...disclosure,
              // Disclosure createdByFullName is not stored in the DB, so we need to
              // keep the existing value
              createdByFullName:
                disclosuresById[disclosure.id ?? ""]?.createdByFullName ?? "",
            }),
          ),
        }
        dispatch(providerSlice.actions.providerUpdated(updatedProvider))

        if (setDataStatus) {
          setDataStatus(DataStatus.SAVED)
        } else {
          dispatch(
            providerLoaderSlice.actions.providerDataStatusUpdated({
              baseProviderDataStatus: DataStatus.SAVED,
            }),
          )
        }
        return {
          providerUpdated: true,
          provider: updatedProvider,
        }
      } else {
        throw new Error(
          `Failed to update base provider with status code: ${response.status}`,
        )
      }
    } catch (error) {
      if (setDataStatus) {
        setDataStatus(DataStatus.ERROR)
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.ERROR,
          }),
        )
      }
      console.error(error)

      const state = getState() as RootState
      const commonText = state.contentManagement.content.common
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: commonText?.errorBannerInternalServerErrorTitle ?? "",
            content: commonText?.errorBannerInternalServerErrorContent ?? "",
          },
        }),
      )
    }
  },
)

export const getProviderPhotoThumbnailEndpoint = (
  providerId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/provider/providers/${providerId}/profilePhoto?getThumbnail=true`
  )
}

export interface GetProviderPhotoArgs {
  photoId: string
}

export interface ProviderImage {
  id: string
  fileStatus: string
}

export const getProviderPhotoThunk = createAsyncThunk(
  "providerLoader/getProviderPhoto",
  async ({ photoId }: GetProviderPhotoArgs, { dispatch, getState }) => {
    const providerImage: ProviderImage = { id: photoId, fileStatus: "" }
    const state = getState() as { provider: ProviderState }
    const provider: Provider = selectProvider(state)
    try {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          baseProviderDataStatus: DataStatus.UPDATING,
        }),
      )

      const url: string = getProviderPhotoThumbnailEndpoint(provider.id!)
      const response = await axios.get(url, { responseType: "blob" })

      let base64String = ""
      if (response.status === 200 || response.status === 202) {
        if (response.status === 200) {
          providerImage.fileStatus = "available"
          const fileBlob: Blob = response.data
          base64String = await blobToBase64(fileBlob)
          // save to local storage
          saveBase64ImageWithEviction(providerImage.id, base64String)
        } else {
          providerImage.fileStatus = "processing"
        }
        const updatedProvider: Provider = {
          ...provider,
          photoFileId: providerImage.id,
          photoFileStatusDisplayId: providerImage.fileStatus,
        }

        dispatch(providerSlice.actions.providerUpdated(updatedProvider))
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.LOADED,
          }),
        )

        return base64String
      } else {
        throw new Error(
          "Failed to fetch office location photo from practice service",
        )
      }
    } catch (error) {
      // catch 410 axios error
      if (error && (error as AxiosError).response?.status === 410) {
        providerImage.fileStatus = "quarantined"
        const upatedProvider: Provider = {
          ...provider,
          photoFileStatusDisplayId: providerImage.fileStatus,
        }

        dispatch(providerSlice.actions.providerUpdated(upatedProvider))
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.LOADED,
          }),
        )
      }
      console.error(error)
    }
  },
)

interface PostProviderPhotoThunkArgs {
  providerId: string
  payload: {
    file: Blob | null
    fileName: string
  }
}

const getProviderPhotosEndpoint = (providerId: string): string => {
  return getBaseEndpoint() + `/provider/providers/${providerId}/files`
}

export const postProviderPhotoThunk = createAsyncThunk(
  "providerLoader/postProviderPhoto",
  async (
    { providerId, payload }: PostProviderPhotoThunkArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const provider: Provider = selectProvider(state)

    dispatch(
      providerLoaderSlice.actions.providerDataStatusUpdated({
        baseProviderDataStatus: DataStatus.UPDATING,
      }),
    )

    try {
      const formData = new FormData()
      formData.append("FileName", payload.fileName)
      formData.append("EntityType", "provider")
      formData.append("EntityId", providerId)

      if (payload.file) {
        formData.append("FileData", payload.file, payload.fileName)
      }

      const url: string = getProviderPhotosEndpoint(providerId)
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      if (response.status === 200) {
        const providerPhotoCreatedByFullName = await getFullUserName(
          response.data.providerPhoto?.createdBy,
          state.contentManagement.content.common?.haAdmin,
        )

        const updatedProvider: Provider = {
          ...provider,
          photoFileId: response.data.id,
          photoFileName: response.data.fileName,
          photoFileStatusDisplayId: response.data.fileStatusDisplayId,
          providerPhoto:
            response.data.fileStatusDisplayId ===
            FileStatusDisplayIdEnum.QUARANTINED
              ? undefined
              : {
                  fileName: response.data.fileName,
                  fileId: response.data.id,
                  createdBy: response.data.createdBy,
                  createdByFullName: providerPhotoCreatedByFullName,
                  createdDateTime: response.data.createdDateTime,
                  fileStatusDisplayId: response.data.fileStatusDisplayId,
                  thumbnailFileId: response.data.thumbnailFileId,
                  thumbnailBase64: response.data.thumbnailBase64,
                },
        }

        dispatch(providerSlice.actions.providerUpdated(updatedProvider))
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.LOADED,
          }),
        )
        // Download thumbnail image
        // Will likely return a "Processing" fileState so we will need to poll until "Available"
        dispatch(
          getProviderPhotoThunk({
            photoId: response.data.id,
          }),
        )

        return updatedProvider
      }
    } catch (error) {
      // Handle upload errors
      appInsightsLogger.traceError(error)
      const state = getState() as RootState
      const cmsContent = selectContent(state)
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: cmsContent?.common?.fileUploadErrorTitle || "",
              content:
                cmsContent?.common
                  ?.errorBannerIncompatibleFileUploadErrorContent || "",
            },
          }),
        )
        return
      } else if (axios.isAxiosError(error) && error.response?.status === 500) {
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title:
                cmsContent?.common?.errorBannerInternalServerErrorTitle || "",
              content:
                cmsContent?.common?.errorBannerInternalServerErrorContent || "",
            },
          }),
        )
        return
      }

      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          baseProviderDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

const deleteProviderPhotoEndpoint = (
  providerId: string,
  photoId: string,
): string => {
  return (
    getBaseEndpoint() + `/provider/providers/${providerId}/files/${photoId}`
  )
}

export interface DeleteProviderPhotoArgs {
  photoId: string
  isHardDelete: boolean
  setDataStatus?: (dataStatus: DataStatus) => void
}

export const deleteProviderPhotoThunk = createAsyncThunk(
  "providerLoader/deleteProviderPhoto",
  async (
    { photoId, isHardDelete, setDataStatus }: DeleteProviderPhotoArgs,
    { dispatch, getState },
  ) => {
    try {
      setDataStatus?.(DataStatus.SENDING)

      localStorage.removeItem(photoId)
      const state = getState() as { provider: ProviderState }
      const provider: Provider = selectProvider(state)
      if (isHardDelete && provider.id) {
        const url: string = deleteProviderPhotoEndpoint(provider.id, photoId)
        await axios.delete(url)
      }
      const updatedProvider: Provider = {
        ...provider,
        photoFileId: undefined,
        photoFileName: undefined,
        photoFileStatusDisplayId: undefined,
        providerPhoto: undefined,
      }
      dispatch(providerSlice.actions.providerUpdated(updatedProvider))
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          baseProviderDataStatus: DataStatus.LOADED,
        }),
      )
      setDataStatus?.(DataStatus.LOADED)
      return true
    } catch (error) {
      console.error(error)
      setDataStatus?.(DataStatus.ERROR)
      return false
    }
  },
)

interface UpdateProviderLicenseArgs {
  payload: Provider
  file: Blob | null
  fileName: string
  setDataStatus?: (dataStatus: DataStatus) => void
}

export const updateProviderLicense = createAsyncThunk(
  "practiceLoader/updateProviderLicense",
  async (
    { payload, file, fileName, setDataStatus }: UpdateProviderLicenseArgs,
    { dispatch, getState },
  ) => {
    try {
      const state = getState() as { provider: ProviderState }
      const provider: Provider = selectProvider(state)
      const photoFileStatusDisplayId = provider.photoFileStatusDisplayId
      const photoFileName = provider.photoFileName

      if (setDataStatus) {
        setDataStatus(DataStatus.PENDING)
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.UPDATING,
          }),
        )
      }
      // add the new provider license
      const response = await axios.put<Provider>(
        getPostBaseProviderEndpoint(provider.id!),
        payload,
      )
      if (response.status === 200 || response.status === 201) {
        // get the updates
        const providerData = response.data
        const updatedProvider: Provider = {
          ...providerData,
          // The provider photo fields below are not stored in the Provider DB table
          // but are part of Provider state.  We need to set them below to ensure
          // their value is not lost.
          photoFileStatusDisplayId: photoFileStatusDisplayId,
          photoFileName: photoFileName,
        }
        dispatch(providerSlice.actions.providerUpdated(updatedProvider))
        // get the newly added license record id
        const addedLicenses = providerData.providerLicenses.filter(
          (obj1) =>
            !payload.providerLicenses.some((obj2) => obj1.id === obj2.id),
        )

        // upload the license for the new record
        if (addedLicenses) {
          dispatch(
            postProviderLicenseThunk({
              providerId: provider.id!,
              file: file,
              fileName: fileName,
              licenseId: addedLicenses[0].id!,
            }),
          )
        }

        if (setDataStatus) {
          setDataStatus(DataStatus.SAVED)
        } else {
          dispatch(
            providerLoaderSlice.actions.providerDataStatusUpdated({
              baseProviderDataStatus: DataStatus.SAVED,
            }),
          )
        }

        return {
          providerUpdated: true,
        }
      } else {
        throw new Error(
          `Failed to update provider license with status code: ${response.status}`,
        )
      }
    } catch (error) {
      if (setDataStatus) {
        setDataStatus(DataStatus.ERROR)
      } else {
        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.ERROR,
          }),
        )
      }
      console.error(error)
    }
  },
)

interface PostProviderLicenseThunkArgs {
  providerId: string
  file: Blob | null
  fileName: string
  licenseId: string
}

export const getProviderLicenseEndpoint = (providerId: string): string => {
  return getBaseEndpoint() + `/provider/providers/${providerId}/files`
}

const updateProviderLicenseStatusByLicenseId = (
  licenses: ProviderLicense[],
  licenseId: string,
  fileStatus: string,
  fileId: string,
  fileName: string,
) => {
  return licenses.map((item) => {
    if (item.id === licenseId) {
      return {
        ...item,
        fileStatusDisplayId: fileStatus,
        fileId: fileId,
        fileName: fileName,
      }
    }
    return item
  })
}

export const postProviderLicenseThunk = createAsyncThunk(
  "providerLoader/postProviderLicense",
  async (
    { providerId, file, fileName, licenseId }: PostProviderLicenseThunkArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as { provider: ProviderState }
    const provider: Provider = selectProvider(state)

    dispatch(
      providerLoaderSlice.actions.providerDataStatusUpdated({
        baseProviderDataStatus: DataStatus.UPDATING,
      }),
    )

    try {
      const formData = new FormData()
      formData.append("FileName", fileName)
      formData.append("EntityType", "license")
      formData.append("EntityId", licenseId)

      if (file) {
        formData.append("FileData", file, fileName)
      }

      const url: string = getProviderLicenseEndpoint(providerId)
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      if (response.status === 200) {
        const updatedLicenses: ProviderLicense[] =
          updateProviderLicenseStatusByLicenseId(
            provider.providerLicenses,
            licenseId,
            "processing",
            response.data.id,
            fileName,
          )

        const payload: Provider = {
          ...provider,
          providerLicenses: updatedLicenses,
        }

        dispatch(updateBaseProvider({ payload }))

        dispatch(
          providerLoaderSlice.actions.providerDataStatusUpdated({
            baseProviderDataStatus: DataStatus.LOADED,
          }),
        )
      }
    } catch (error) {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          baseProviderDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)

interface GetProviderFileThunkArgs {
  providerId: string
  fileId: string
  fileSecurityScanErrorText: string
}

export const getProviderFileEndpoint = (
  providerId: string,
  fileId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/provider/providers/${providerId}/files/${fileId}/content`
  )
}

const updateProviderLicenseStatusById = (
  licenses: ProviderLicense[],
  fileId: string,
  fileStatus: string,
) => {
  return licenses.map((item) => {
    if (item.fileId === fileId) {
      return { ...item, fileStatusDisplayId: fileStatus }
    }
    return item
  })
}

export const getProviderFileThunk = createAsyncThunk(
  "providerLoader/getProviderFile",
  async (
    { providerId, fileId, fileSecurityScanErrorText }: GetProviderFileThunkArgs,
    { dispatch, getState },
  ) => {
    const providerFile: ProviderImage = { id: fileId, fileStatus: "" }
    const state = getState() as { provider: ProviderState }
    const provider: Provider = selectProvider(state)

    try {
      dispatch(
        providerLoaderSlice.actions.providerDataStatusUpdated({
          baseProviderDataStatus: DataStatus.UPDATING,
        }),
      )

      const url: string = getProviderFileEndpoint(providerId, fileId)
      const response = await axios.get(url, { responseType: "blob" })

      const licenseToUpdate = provider.providerLicenses.find((obj) => {
        obj.fileId == fileId
      })

      if (response.status === 200 || response.status === 202) {
        if (response.status === 200) {
          providerFile.fileStatus = "available"
        } else {
          providerFile.fileStatus = "processing"
        }

        // Update base provider when the file status changes
        if (licenseToUpdate?.fileStatusDisplayId !== providerFile.fileStatus) {
          const updatedLicenses: ProviderLicense[] =
            updateProviderLicenseStatusById(
              provider.providerLicenses,
              fileId,
              providerFile.fileStatus,
            )

          const payload: Provider = {
            ...provider,
            providerLicenses: updatedLicenses,
          }
          dispatch(updateBaseProvider({ payload }))
        }
      } else {
        throw new Error("Failed to fetch license file from provider service")
      }
    } catch (error) {
      // catch 410 axios error
      if (error && (error as AxiosError).response?.status === 410) {
        // Send quarantined message to error banner
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: "",
              content: fileSecurityScanErrorText,
            },
          }),
        )

        // // Remove the quarantined license record
        const updatedItems = provider.providerLicenses.filter(
          (item) => item.fileId! !== fileId,
        )

        if (updatedItems.length > 0) {
          const payload: Provider = {
            ...provider,
            providerLicenses: updatedItems,
          }

          dispatch(updateBaseProvider({ payload }))
        }
      }
      console.error(error)
    }
  },
)

interface GetProviderLicenseFilesZipArgs {
  providerId: string | undefined
  downloadFileNameNoExt: string
  licenseFileData: Array<{
    licenseFileId: string
    fileName: string
  }>
  setDataStatus?: (status: DataStatus) => void
}
export const getProviderLicenseFilesZip = createAsyncThunk(
  "providerLoader/getProviderLicenseFilesZip",
  async (
    {
      providerId,
      downloadFileNameNoExt,
      licenseFileData,
      setDataStatus,
    }: GetProviderLicenseFilesZipArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const commonText = state.contentManagement.content.common

    dispatch(bannersReset())

    const providerLicenseDownloadInfo = licenseFileData.map((fileDatum) => ({
      fileName: fileDatum.fileName,
      downloadUrl: `${getBaseEndpoint()}/provider/providers/${providerId}/files/${fileDatum.licenseFileId}/content`,
    }))
    const downloadSuccess = await downloadZip({
      fileDownloadInfo: providerLicenseDownloadInfo,
      downloadFileNameNoExt,
      setDataStatus,
    })

    if (!downloadSuccess) {
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: "Error",
            content:
              commonText?.fileDownloadError || "Failed to download files",
          },
        }),
      )
    }
  },
)

interface GetProviderLiabilityFilesZipArgs {
  providerId: string | undefined
  downloadFileNameNoExt: string
  liabilityFileData: Array<{
    liabilityFileId: string
    fileName: string
  }>
  setDataStatus?: (status: DataStatus) => void
}

export const getProviderLiabilityFilesZip = createAsyncThunk(
  "providerLoader/getProviderLiabilityFilesZip",
  async (
    {
      providerId,
      liabilityFileData,
      downloadFileNameNoExt,
      setDataStatus,
    }: GetProviderLiabilityFilesZipArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const commonText = state.contentManagement.content.common

    dispatch(bannersReset())

    const providerLiabilityDownloadInfo = liabilityFileData.map(
      (fileDatum) => ({
        fileName: fileDatum.fileName,
        downloadUrl: `${getBaseEndpoint()}/provider/providers/${providerId}/files/${fileDatum.liabilityFileId}/content`,
      }),
    )
    const downloadSuccess = await downloadZip({
      fileDownloadInfo: providerLiabilityDownloadInfo,
      downloadFileNameNoExt,
      setDataStatus,
    })

    if (!downloadSuccess) {
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: commonText?.errorBannerTitle || "Error",
            content:
              commonText?.fileDownloadError || "Failed to download files",
          },
        }),
      )
    }
  },
)
